import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { InputField } from "components";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import TableDataComponent from "components/organisms/TableData";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import DataLabBKK from "services/DataLabBKK";
import { PageMode, RDTBrandOptions } from "utils/constant";
import { confirmAlert } from "react-confirm-alert";
import { errorToastNotif, successToastNotif } from "utils/common";

const DataLabService = new DataLabBKK();

const TableComponent = ({ handlePageModeChange, handleEditData }) => {
  const [state, setState] = useState({
    page: 1,
    limit: 10,
    searchKeyword: "",
    isLoading: true,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const { currentUser } = useSelector((state) => state.authReducer);

  const getAllDataLab = async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      let search = "";

      if (state.searchKeyword !== "") {
        search = `${state.searchKeyword}`;
      }

      const response = await DataLabService.getAll({
        page: state.page,
        limit: state.limit,
        search,
      });
      setState((prevState) => ({
        ...prevState,
        data: response.bkk_data_dasar_lab,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };

  useEffect(() => {
    getAllDataLab();
  }, [state.page, state.searchKeyword]);

  const onPageChange = (page) => {
    setState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await DataLabService.remove(id)
              .then((res) => {
                getAllDataLab();
                successToastNotif("Data berhasil dihapus");
              })
              .catch((err) => {
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => handleEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.kd_id)}
            btnType={"delete"}
          />
        </div>
      ),
    },
    {
      header: "Tgl disimpan",
      accessorKey: "tgl_simpan",
      cell: ({ getValue }) =>
        getValue() ? moment(getValue()).format("YYYY-MM-DD") : null,
    },
    {
      header: "Provinsi",
      accessorKey: "nama_prop",
    },
    {
      header: "Kabupaten",
      accessorKey: "nama_kab",
    },
    {
      header: "Wilker",
      accessorKey: "nama_faskes",
    },
    {
      header: "Pemeriksaan mikroskopis",
      accessorKey: "priksa_mikros",
    },
    {
      header: "Jum mikroskopis keadaan baik",
      accessorKey: "jum_mikros_baik",
    },
    {
      header: "Melaksanakan pemeriksaan RDT",
      accessorKey: "keg_prksa_rdt",
    },
    {
      header: "SPO pembersihan dan penyimpanan kaca objek",
      accessorKey: "spo_kaca_objek",
    },
    {
      header: "SPO pembuatan sediaan darah",
      accessorKey: "spo_pmbtn_darah",
    },
    {
      header: "SPO pewarnaan sediaan darah",
      accessorKey: "spo_pwrn_darah",
    },
    {
      header: "SPO pembacaan dan pelaporan hasil sediaan darah malaria",
      accessorKey: "spo_pbcn_darah",
    },
    {
      header: "SPO uji kualitas giemsa",
      accessorKey: "spo_giemsa",
    },
    {
      header: "SPO uji kualitas minyak imersi",
      accessorKey: "spo_imersi",
    },
    {
      header: "SPO uji kualitas methanol",
      accessorKey: "spo_methanol",
    },
    {
      header: "SPO uji kualitas pH buffer",
      accessorKey: "spo_phbuffer",
    },
    {
      header: "SPO penggunaan dan perawatan mikroskop",
      accessorKey: "spo_pgunaan_prwtan_mikros",
    },
    {
      header: "SPO pelaksanaan K3",
      accessorKey: "spo_k3malaria",
    },
    {
      header: "SPO uji RDT",
      accessorKey: "spo_rdt",
    },
    {
      header: "Merek RDT yang digunakan",
      accessorKey: "kd_rdt",
      cell: ({ getValue }) =>
        getValue()
          ? RDTBrandOptions.find((opt) => opt.value === getValue())?.label ||
            null
          : null,
    },
    {
      header: "Merek RDT lainnya",
      accessorKey: "merk_rdt_lain",
      cell: ({ getValue }) => getValue() || "-",
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: state?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">
        Data Dasar Laboratorium Pemeriksaan Malaria - BKK
      </h1>
      <h6 className="text-danger">
        #Catatan: Hanya di-input Satu Kali. Apabila terdapat perubahan data,
        lakukan Edit data.
      </h6>
      <div
        className="action-wrapper"
        style={{ marginTop: "10px", marginBottom: "24px" }}
      >
        <div className="action-button">
          {currentUser && currentUser.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => handlePageModeChange(PageMode.add)}
            >
              Tambah Data Dasar Laboratorium
            </Button>
          )}
        </div>
      </div>
      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={state.isLoading}
        currentPage={state.currentPage}
        limit={state.limit}
        nextPage={state.nextPage}
        previousPage={state.previousPage}
        total={state.totalData}
      />
    </div>
  );
};

export default TableComponent;
